// src/components/Sidebar.jsx
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../css/Sidebar.css";

const items = [
  ["dashboard", "fa-home", "Dashboard"],
  ["revenue", "fa-chart-line", "Revenue"],
  ["users", "fa-users", "Users"],
  [
    "finance",
    "fa-university",
    "Finance",
    [
      { to: "/finance/wallet",       icon: "fa-wallet",           label: "Wallet" },
      { to: "/finance/withdrawals",  icon: "fa-arrow-circle-down",label: "Withdrawals" },
      { to: "/finance/pg-log",       icon: "fa-list",             label: "PG Log" },
      { to: "/finance/reports",      icon: "fa-file-alt",         label: "Reports" },
    ],
  ],
  [
    "services",
    "fa-layer-group",
    "Services",
    [
      {
        group: "Transport", key: "transport", icon: "fa-car",
        items: [
          { to: "/services/transport/driver-approvals", icon: "fa-user-check", label: "Driver Approvals" },
          { to: "/services/transport/rides",            icon: "fa-car",        label: "Rides" },
          { to: "/services/transport/ride-types",       icon: "fa-tags",       label: "Ride Types" },
        ],
      },
      {
        group: "Food", key: "food", icon: "fa-utensils",
        items: [
          { to: "/services/food/orders",     icon: "fa-receipt", label: "Orders" },
          { to: "/services/food/food-types", icon: "fa-tags",    label: "Food Types" },
        ],
      },
      {
        group: "Mart", key: "mart", icon: "fa-shopping-basket",
        items: [
          { to: "/services/mart/orders",        icon: "fa-receipt", label: "Orders" },
          { to: "/services/mart/product-types", icon: "fa-tags",    label: "Product Types" },
        ],
      },
      {
        group: "Entertainment", key: "entertainment", icon: "fa-theater-masks",
        items: [
          { to: "/services/events",            icon: "fa-theater-masks", label: "Events" },
          { to: "/services/events/bookings",   icon: "fa-ticket-alt",    label: "Bookings" },
          { to: "/services/events/revenue",    icon: "fa-chart-bar",     label: "Revenue" },
          { to: "/services/events/banners",    icon: "fa-image",         label: "Banners" },
          { to: "/services/events/organizers", icon: "fa-building",      label: "Organizers" },
        ],
      },
      {
        group: "Dining Out", key: "dining", icon: "fa-utensils",
        items: [
          { to: "/services/dining/reservations",    icon: "fa-utensils", label: "Reservations" },
          { to: "/services/dining/restaurant-types",icon: "fa-store",    label: "Restaurant Types" },
        ],
      },
      {
        group: "Hotel", key: "hotel", icon: "fa-hotel",
        items: [
          { to: "/services/hotel/bookings",   icon: "fa-hotel", label: "Bookings" },
          { to: "/services/hotel/room-types", icon: "fa-bed",   label: "Room Types" },
        ],
      },
    ],
  ],
  ["Feedback", "fa-comment-dots", "Feedback"],
  ["master-management", "fa-cogs", "Management"],
  ["logs", "fa-list", "Logs"],
];

const isMobile = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 992px)").matches;

// Finance: only these service groups are visible
const FINANCE_ALLOWED_GROUPS = new Set(["transport", "food", "mart", "entertainment"]);
// Finance: within allowed groups, only these paths
const FINANCE_ALLOWED_PATHS = new Set([
  "/services/transport/rides",
  "/services/food/orders",
  "/services/mart/orders",
  "/services/events/bookings",
]);

const FINANCE_HIDDEN_TOP_LEVEL = new Set(["users", "Feedback"]);

// map path prefix → group key for auto-open
const PATH_TO_GROUP = {
  "/services/transport": "transport",
  "/services/food":      "food",
  "/services/mart":      "mart",
  "/services/events":    "entertainment",
  "/services/dining":    "dining",
  "/services/hotel":     "hotel",
};

export default function Sidebar() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [openServices, setOpenServices] = useState(() => {
    // auto-open the group matching the initial path
    const path = window.location.pathname;
    const key = Object.entries(PATH_TO_GROUP).find(([prefix]) => path.startsWith(prefix))?.[1];
    return key ? { [key]: true } : {};
  });
  const [open, setOpen] = useState(false);
  const [isSuper, setIsSuper] = useState(() => {
    try {
      const v = localStorage.getItem("is_super");
      if (v === "true" || v === "1") return true;
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
      const roleNorm = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
      const roleCompact = roleNorm.replace(/\s+/g, "");
      return roleNorm === "super admin" || roleCompact === "superadmin";
    } catch {
      return false;
    }
  });
  const [isFinance, setIsFinance] = useState(() => {
    try {
      if (localStorage.getItem("is_finance") === "true") return true;
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
      return String(raw).toLowerCase().replace(/\s+/g, " ").trim() === "finance";
    } catch {
      return false;
    }
  });

  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const wasServices = prevPath.current.startsWith("/services");
    const isServices = location.pathname.startsWith("/services");
    setServicesOpen(isServices);

    // auto-open the matching service group
    const groupKey = Object.entries(PATH_TO_GROUP).find(([prefix]) => location.pathname.startsWith(prefix))?.[1];
    if (groupKey) setOpenServices(prev => ({ ...prev, [groupKey]: true }));

    const wasFinance = prevPath.current.startsWith("/finance");
    const isFinance = location.pathname.startsWith("/finance");
    setFinanceOpen(isFinance);

    if (isMobile()) {
      if (wasServices && !isServices) setOpen(false);
      if (wasFinance && !isFinance) setOpen(false);
    }

    prevPath.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (open && isMobile()) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  useEffect(() => {
    const onResize = () => {
      if (!isMobile() && open) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  useEffect(() => {
    const onStorage = (e) => {
      if (["user", "is_super", "is_finance"].includes(e.key)) {
        try {
          const v = localStorage.getItem("is_super");
          if (v === "true" || v === "1") return setIsSuper(true);
          const user = JSON.parse(localStorage.getItem("user") || "null");
          const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
          const roleNorm = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
          const roleCompact = roleNorm.replace(/\s+/g, "");
          setIsSuper(roleNorm === "super admin" || roleCompact === "superadmin");
          setIsFinance(
            localStorage.getItem("is_finance") === "true" ||
            roleNorm === "finance"
          );
        } catch {
          setIsSuper(false);
          setIsFinance(false);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Re-read roles when Login fires auth-role-changed in the SAME tab
  useEffect(() => {
    const onRoleChanged = (e) => {
      const { isSuper: sup, isFinance: fin } = e.detail || {};
      setIsSuper(!!sup);
      setIsFinance(!!fin);
    };
    window.addEventListener("auth-role-changed", onRoleChanged);
    return () => window.removeEventListener("auth-role-changed", onRoleChanged);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isMobile()) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const getOpenFor = (key) =>
    key === "services" ? servicesOpen : key === "finance" ? financeOpen : false;

  const toggleOpenFor = (key) => {
    if (key === "services") setServicesOpen((o) => !o);
    if (key === "finance") setFinanceOpen((o) => !o);
  };

  const MOBILE_HEADER_OFFSET = 64;
  const toggleStyle = isMobile()
    ? { top: `calc(env(safe-area-inset-top) + ${MOBILE_HEADER_OFFSET}px)` }
    : undefined;

  const handleNavClick = () => {
    if (isMobile()) setOpen(false);
  };

  return (
    <>
      <button
        className="sidebar-toggle"
        aria-label="Toggle sidebar"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={toggleStyle}
      >
        <i className={`fa ${open ? "fa-times" : "fa-bars"}`} />
      </button>

      <nav
        className={`sidebar bg-dark text-white ${open ? "open" : ""}`}
        aria-hidden={!open && isMobile()}
      >
        <div className="sidebar__head">
          <h4 className="m-0">སྟབས་བདེ Admin</h4>
        </div>

        <div className="sidebar__scroll">
          <ul className="nav flex-column list-unstyled px-3">
            {items.map((item) => {
              // Hide top-level items that finance cannot access
              if (isFinance && FINANCE_HIDDEN_TOP_LEVEL.has(item[0])) return null;

              if (Array.isArray(item[3])) {
                const key = item[0];
                const isOpen = getOpenFor(key);
                // For finance: filter services sub-items; show all finance sub-items
                const subItems = item[3];

                return (
                  <li className="nav-item mb-1" key={key}>
                    <button
                      type="button"
                      className={`nav-link w-100 text-start d-flex align-items-center justify-content-between ${
                        isOpen ? "active" : ""
                      }`}
                      onClick={() => toggleOpenFor(key)}
                      aria-expanded={isOpen}
                      aria-controls={`${key}-submenu`}
                    >
                      <span>
                        <i className={`fa ${item[1]} me-2`} /> {item[2]}
                      </span>
                      <i
                        className={`fa fa-chevron-${isOpen ? "down" : "right"} ms-1`}
                      />
                    </button>

                    <div
                      id={`${key}-submenu`}
                      className={`sidebar__submenu ${isOpen ? "open" : ""}`}
                    >
                      <ul className="nav flex-column list-unstyled ms-2">
                        {/* ---- Finance: same nested groups, filtered to allowed paths ---- */}
                        {isFinance && key === "services" ? (
                          subItems
                            .filter(g => FINANCE_ALLOWED_GROUPS.has(g.key))
                            .map(g => {
                              const allowedLinks = g.items.filter(link => FINANCE_ALLOWED_PATHS.has(link.to));
                              if (!allowedLinks.length) return null;
                              const gOpen = !!openServices[g.key];
                              return (
                                <li key={g.key} className="nav-item mb-1">
                                  <button
                                    type="button"
                                    className={`nav-link w-100 text-start d-flex align-items-center justify-content-between ps-2 ${gOpen ? "active" : ""}`}
                                    style={{ fontSize: "0.875rem" }}
                                    onClick={() => setOpenServices(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                                  >
                                    <span><i className={`fa ${g.icon} me-2`} />{g.group}</span>
                                    <i className={`fa fa-chevron-${gOpen ? "down" : "right"} ms-1`} style={{ fontSize: "0.7rem" }} />
                                  </button>
                                  <div className={`sidebar__submenu ${gOpen ? "open" : ""}`}>
                                    <ul className="nav flex-column list-unstyled ms-2">
                                      {allowedLinks.map(link => (
                                        <li key={link.to}>
                                          <NavLink to={link.to} className={({ isActive }) => `nav-link ps-4 ${isActive ? "active" : ""}`} onClick={handleNavClick}>
                                            <i className={`fa ${link.icon} me-2`} /> {link.label}
                                          </NavLink>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </li>
                              );
                            })
                        ) : key === "services" ? (
                          /* ---- Services: nested group dropdowns ---- */
                          subItems.map(g => {
                            const gOpen = !!openServices[g.key];
                            return (
                              <li key={g.key} className="nav-item mb-1">
                                <button
                                  type="button"
                                  className={`nav-link w-100 text-start d-flex align-items-center justify-content-between ps-2 ${gOpen ? "active" : ""}`}
                                  style={{ fontSize: "0.875rem" }}
                                  onClick={() => setOpenServices(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                                >
                                  <span><i className={`fa ${g.icon} me-2`} />{g.group}</span>
                                  <i className={`fa fa-chevron-${gOpen ? "down" : "right"} ms-1`} style={{ fontSize: "0.7rem" }} />
                                </button>
                                <div className={`sidebar__submenu ${gOpen ? "open" : ""}`}>
                                  <ul className="nav flex-column list-unstyled ms-2">
                                    {g.items.map(link => (
                                      <li key={link.to}>
                                        <NavLink to={link.to} className={({ isActive }) => `nav-link ps-4 ${isActive ? "active" : ""}`} onClick={handleNavClick}>
                                          <i className={`fa ${link.icon} me-2`} /> {link.label}
                                        </NavLink>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </li>
                            );
                          })
                        ) : (
                          /* ---- Finance / other flat dropdowns ---- */
                          subItems.map((sub, i) =>
                            sub.section ? (
                              <li key={sub.section + i} className="text-uppercase small fw-bold mt-3 mb-1 text-secondary px-2">{sub.section}</li>
                            ) : (
                              (sub.to === "/finance/wallet" && !isSuper) ? null : (
                                <li key={sub.to}>
                                  <NavLink to={sub.to} className={({ isActive }) => `nav-link ps-4 ${isActive ? "active" : ""}`} onClick={handleNavClick}>
                                    <i className={`fa ${sub.icon} me-2`} /> {sub.label}
                                  </NavLink>
                                </li>
                              )
                            )
                          )
                        )}
                      </ul>
                    </div>
                  </li>
                );
              }

              return (
                <li className="nav-item mb-1" key={item[0]}>
                  <NavLink
                    to={`/${item[0]}`}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleNavClick}
                  >
                    <i className={`fa ${item[1]} me-2`} /> {item[2]}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="sidebar__fade" />
        </div>
      </nav>

      <div
        className={`sidebar-backdrop ${open ? "show" : ""}`}
        onClick={() => setOpen(false)}
      />
    </>
  );
}
